/**
 * TNK-140 phase B — Review step. Wide-row layout per import item:
 * image left, matched attributes center, qty + condition right,
 * confidence badge in the image corner, Quick View button per row.
 *
 * Sits between Upload (the /$id index page — photo verification) and
 * Commit (the /$id/results page — post-commit landing). The operator
 * picks Bin + Lot here too — the final inputs before committing.
 *
 * If the operator navigates here for a draft that's already committed,
 * we bounce to /results. If they land here while enrichment is still
 * in flight, we bounce back to Upload so they finish photo review
 * first.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Eye,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { FIGMA } from "@/components/import/figmaStyles";
import { ImportStepper } from "@/components/import/ImportStepper";
import { QuickViewModal } from "@/components/import/QuickViewModal";
import { PrimaryButton } from "@/components/PrimaryButton";
import { asRarity, rarityConfig } from "@/components/shop/cardVisuals";
import type { CatalogCandidate } from "@/features/catalogSearch";
import { calcLandedCost, useApp } from "@/context/AppContext";
import { imageUrl } from "@/features/imageUpload";
import { identifyCardWithVision } from "@/features/cardVisionClient";
import { apiFetch } from "@/lib/apiClient";
import { containerPath, formatContainerPath } from "@/features/containerPath";
import { computeMatchConfidence, confidenceTone } from "@tcg-web/normalize";
import {
  confirmImportDraft,
  getImportDraft,
  overrideImportItem,
  patchImportItem,
  type ApiImportDraft,
  type ApiImportDraftItem,
} from "@/features/importsApi";

export const Route = createFileRoute("/tools/imports/$id/review")({
  component: ImportReviewPage,
});

interface ParsedShape {
  name?: string;
  setName?: string;
  setCode?: string;
  cardNumber?: string;
  rarity?: string;
  condition?: string;
  quantity?: number;
  marketPrice?: number;
  game?: string;
  year?: string;
  finish?: string | null;
  tcgSupertype?: string;
  tcgArtist?: string;
  tcgTypes?: string[];
  /** TNK-140 phase D.3 — catalog stock image URL surfaced from the
   *  card_catalog row matching this item. Populated on override + on
   *  GET via server-side backfill (transient). */
  matchImageUrl?: string;
  matchImageLargeUrl?: string;
  /** TNK-140 phase E — Magic color identity for the rarity-tinted row
   *  border. White/Blue/Black/Red/Green codes; gradient for multi. */
  mtgColors?: string[];
  /** TNK-194/216 scan drafts — the vision result's self-assessed confidence +
   *  review flag, written into parsed at identify. Drives the scan row badge
   *  (CSV imports score against the CSV columns instead). */
  confidence?: "high" | "medium" | "low";
  needsReview?: boolean;
}

type FinishOption = "Normal" | "Holo" | "Reverse Holo" | "Premium";
const FINISH_OPTIONS: FinishOption[] = [
  "Normal",
  "Holo",
  "Reverse Holo",
  "Premium",
];

// Canonical MTG color pip backgrounds — same palette as ManaCost.tsx
// so the row border reads as the card's identity at a glance.
const MTG_COLORS: Record<string, string> = {
  W: "#f6efd2",
  U: "#9fd0f0",
  B: "#3f3a3a",
  R: "#f0998d",
  G: "#a3cba0",
};

/**
 * Build a border background for a Magic row. Single color → solid;
 * two colors → 50/50 gradient; multi → evenly-divided gradient; no
 * colors → muted gray (colorless / artifact). Mirrors the shop's
 * card-pair design.
 */
function mtgBorderGradient(colors: string[] | undefined): string | null {
  if (!colors || colors.length === 0) return "#5a5a5a";
  if (colors.length === 1) return MTG_COLORS[colors[0]] ?? "#5a5a5a";
  const stops = colors.map(
    (c, i) =>
      `${MTG_COLORS[c] ?? "#5a5a5a"} ${(i / colors.length) * 100}% ${((i + 1) / colors.length) * 100}%`,
  );
  return `linear-gradient(135deg, ${stops.join(", ")})`;
}

const CONDITIONS = ["NM", "LP", "MP", "HP", "Damaged"] as const;
type ConditionOption = (typeof CONDITIONS)[number];

/**
 * Pull the operator's original name / set / number out of the raw CSV
 * row. eBay File Exchange uses prefixed keys like `*c:set` and
 * `*c:card number`; PriceCharting + generic CSVs use plain
 * `setname` / `cardnumber`. We try every common variant so per-
 * candidate scoring sees the operator's CSV intent regardless of
 * upload format.
 */
function pickCsvFields(row: Record<string, string>): {
  name?: string;
  setName?: string;
  cardNumber?: string;
} {
  const get = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = row[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    return undefined;
  };
  return {
    name: get("*c:card name", "c:card name", "name", "*title", "title"),
    setName: get(
      "*c:set",
      "c:set",
      "setname",
      "set name",
      "setName",
      "console-name",
    ),
    cardNumber: get(
      "*c:card number",
      "c:card number",
      "cardnumber",
      "card number",
      "number",
    ),
  };
}

function ImportReviewPage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { containers, lots } = useApp();

  const [draft, setDraft] = useState<ApiImportDraft | null>(null);
  const [items, setItems] = useState<ApiImportDraftItem[]>([]);
  const [error, setError] = useState<string>("");

  const [batchBinId, setBatchBinId] = useState<string>("");
  const [batchLotId, setBatchLotId] = useState<string>("");
  const [confirming, setConfirming] = useState(false);

  // TNK-196: an import can target any leaf container (bin/box/tab/binder), not
  // just bins. The confirm endpoint accepts any container id; we show the full
  // path so a tab inside a box is unambiguous.
  const containerById = useMemo(
    () => new Map(containers.map((c) => [c.id, c])),
    [containers],
  );
  const targetOptions = useMemo(
    () =>
      containers
        .filter(
          (c) =>
            ["bin", "box", "tab", "binder"].includes(c.kind) && !c.archivedAt,
        )
        .map((c) => ({
          id: c.id,
          label: formatContainerPath(containerPath(c.id, containerById)),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [containers, containerById],
  );

  // TNK-140 phase C — Quick View modal state. `quickViewItem` is the
  // ApiImportDraftItem whose row was clicked; phase D will hand the
  // selected candidate back to a real PATCH.
  const [quickViewItem, setQuickViewItem] = useState<ApiImportDraftItem | null>(
    null,
  );
  // TNK-216 — scan items whose vision call is being re-run (by item id). A scan
  // can land here with a blank name if vision failed/blipped; "Re-run vision"
  // retries the identify on the stored image.
  const [revisioning, setRevisioning] = useState<Set<string>>(new Set());

  // Load draft + items. A CSV draft loads once. A scan draft, though, identifies
  // in the BACKGROUND (~21 cards/min) — and is exempt from the bounce-to-Upload
  // guard below — so if the operator lands here mid-run we must keep refreshing,
  // or the page freezes on an all-blank snapshot while the server fills every
  // card in behind it (TNK-217 — observed twice on a 114-card Magic batch).
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const state = await getImportDraft(id);
        if (cancelled) return;
        if (state.draft.status === "committed") {
          void navigate({
            to: "/tools/imports/$id/results",
            params: { id },
            replace: true,
          });
          return;
        }
        // CSV imports: a pending/enriching draft means the worker hasn't
        // settled — kick back to Upload to finish photo review. Scan drafts are
        // EXEMPT: a "pending" scan item just means vision failed/blipped for
        // that card (a reviewable blank with "Re-run vision"), not in-flight
        // enrichment — so one straggler must not bounce the whole batch review.
        // TNK-194/216.
        if (
          state.draft.kind !== "scan" &&
          (state.draft.status === "enriching" ||
            state.draft.status === "parsing" ||
            state.counters.pending > 0 ||
            state.counters.enriching > 0)
        ) {
          void navigate({
            to: "/tools/imports/$id",
            params: { id },
            replace: true,
          });
          return;
        }
        setError("");
        setDraft(state.draft);
        setItems(state.items);
        // Worker still running → poll until it flips the draft status→ready.
        if (state.draft.kind === "scan" && state.draft.status === "enriching")
          timer = setTimeout(() => void load(), 3000);
      } catch (err) {
        if (!cancelled)
          setError((err as Error).message ?? "Couldn't load draft.");
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, navigate]);

  // A batch is inserted in one transaction, so all items share the same
  // createdAt — fall back to id so the row order is stable across reloads
  // (and matches the server's orderBy) instead of shuffling on every fetch.
  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
      ),
    [items],
  );

  // TNK-216 — a scan draft routes through this same review screen. Its items
  // have no CSV row: the vision read in `parsed` IS the source, and confidence
  // comes from the vision result rather than CSV-vs-catalog scoring.
  const isScan = draft?.kind === "scan";

  const totalUnits = useMemo(
    () =>
      sortedItems.reduce((n, it) => {
        const p = (it.parsed ?? {}) as ParsedShape;
        return n + (p.quantity ?? 1);
      }, 0),
    [sortedItems],
  );

  const scoredItems = useMemo(
    () =>
      sortedItems.map((it) => {
        const p = (it.parsed ?? {}) as ParsedShape;
        if (isScan) {
          // Scan: the vision read is the source; map its confidence to the
          // same 0–100 scale the badge/ready-counter use. A blank row (vision
          // failed / not yet identified) is never "ready" — flag it low so it
          // counts toward review and reads as needing the "Re-run vision" /
          // lookup affordance, not a safe-to-commit match.
          const score = !(p.name ?? "").trim()
            ? 20
            : p.confidence === "high"
              ? 95
              : p.confidence === "medium"
                ? 70
                : p.confidence === "low"
                  ? 40
                  : p.needsReview
                    ? 40
                    : 95;
          return { item: it, parsed: p, score };
        }
        const csv = pickCsvFields(it.csvRow ?? {});
        const hasCatalogHit =
          !!it.imageFrontRef ||
          !!p.tcgSupertype ||
          !!p.tcgArtist ||
          (p.tcgTypes?.length ?? 0) > 0;
        const score = computeMatchConfidence({
          csvName: csv.name,
          csvSetName: csv.setName,
          csvCardNumber: csv.cardNumber,
          parsedName: p.name,
          parsedSetName: p.setName,
          parsedCardNumber: p.cardNumber,
          hasCatalogHit,
        });
        return { item: it, parsed: p, score };
      }),
    [sortedItems, isScan],
  );

  const readyCount = scoredItems.filter((s) => s.score >= 90).length;
  const reviewCount = scoredItems.filter((s) => s.score < 75).length;

  const handleField = async (
    item: ApiImportDraftItem,
    patch: Partial<ParsedShape>,
  ) => {
    setError("");
    try {
      const next = await patchImportItem(
        id,
        item.id,
        patch as Record<string, unknown>,
      );
      setItems((prev) => prev.map((it) => (it.id === next.id ? next : it)));
    } catch (err) {
      setError((err as Error).message ?? "Edit failed.");
    }
  };

  // TNK-216 — re-run the vision identify on a scan item's stored image (for a
  // row that came back blank). Fetches the image as a blob, re-identifies with
  // the item's game, and patches the parsed result. Keeps the scan photo
  // (patch, never override). No-op without an image.
  const revisionItem = async (item: ApiImportDraftItem) => {
    if (!item.imageFrontRef || revisioning.has(item.id)) return;
    setError("");
    setRevisioning((s) => new Set(s).add(item.id));
    try {
      const url = imageUrl(item.imageFrontRef);
      if (!url) throw new Error("Couldn't resolve the scan image.");
      const resp = await apiFetch(url);
      if (!resp.ok) throw new Error("Couldn't load the scan image to re-scan.");
      const blob = await resp.blob();
      const game = ((item.parsed ?? {}) as ParsedShape).game ?? "Pokemon";
      const result = await identifyCardWithVision(blob, null, null, game);
      if (!result || !result.name) {
        setError(
          "Re-scan still couldn't read this card — set it with View / Lookup.",
        );
        return;
      }
      const patch: Partial<ParsedShape> = {
        name: result.name,
        setName: result.setName ?? undefined,
        setCode: result.setCode ?? undefined,
        cardNumber: result.number ?? undefined,
        rarity: result.rarity ?? undefined,
        confidence: result.confidence,
        needsReview: result.needsReview,
      };
      const next = await patchImportItem(
        id,
        item.id,
        patch as Record<string, unknown>,
      );
      setItems((prev) => prev.map((it) => (it.id === next.id ? next : it)));
    } catch (err) {
      setError((err as Error).message ?? "Re-scan failed.");
    } finally {
      setRevisioning((s) => {
        const n = new Set(s);
        n.delete(item.id);
        return n;
      });
    }
  };

  const handleConfirm = async () => {
    if (!draft) return;
    setConfirming(true);
    setError("");
    try {
      await confirmImportDraft(id, {
        binId: batchBinId || null,
        lotId: batchLotId || null,
      });
      void navigate({
        to: "/tools/imports/$id/results",
        params: { id },
        replace: true,
      });
    } catch (err) {
      setError((err as Error).message ?? "Confirm failed.");
      setConfirming(false);
    }
  };

  if (error && !draft) {
    return (
      <section className="flex h-full flex-col space-y-4">
        <BackButton fallback="/tools/imports" />
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      </section>
    );
  }

  if (!draft) {
    return (
      <section className="flex h-full flex-col items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
        <p className="mt-3 text-sm">Loading review…</p>
      </section>
    );
  }

  return (
    <section className="flex min-h-full flex-col space-y-6">
      <BackButton fallback={isScan ? "/tools/scan" : "/tools/imports"} />

      {/* The CSV wizard stepper doesn't apply to scan sessions (TNK-216). */}
      {!isScan && <ImportStepper currentStep="review" importId={id} />}

      {/* Background identify still running — make it unmistakable that the
          blanks are in-progress, not failures, and that the page self-updates. */}
      {isScan && draft.status === "enriching" && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-primary/5 px-3 py-2 text-sm text-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
          <span>
            Identifying cards in the background —{" "}
            <span style={{ fontFamily: FIGMA.fontMono }}>
              {
                sortedItems.filter((it) =>
                  ((it.parsed as ParsedShape)?.name ?? "").trim(),
                ).length
              }
            </span>
            /
            <span style={{ fontFamily: FIGMA.fontMono }}>
              {sortedItems.length}
            </span>{" "}
            done. Safe to leave; this list fills in as cards finish.
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1
            className="text-2xl"
            style={{
              fontFamily: FIGMA.fontHeading,
              fontWeight: 700,
              color: FIGMA.fg,
              letterSpacing: "0.02em",
            }}
          >
            Review matches
          </h1>
          <p className="mt-1 text-sm" style={{ color: FIGMA.muted }}>
            {draft.sourceFilename ?? (isScan ? "Scanned cards" : "Pasted CSV")}{" "}
            ·{" "}
            <span style={{ fontFamily: FIGMA.fontMono }}>
              {sortedItems.length}
            </span>{" "}
            row{sortedItems.length === 1 ? "" : "s"} ·{" "}
            <span style={{ fontFamily: FIGMA.fontMono }}>{totalUnits}</span>{" "}
            total unit{totalUnits === 1 ? "" : "s"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SummaryChip count={readyCount} tone="success" label="ready" />
          {reviewCount > 0 && (
            <SummaryChip count={reviewCount} tone="warning" label="to review" />
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() =>
          void navigate(
            isScan
              ? { to: "/tools/scan" }
              : { to: "/tools/imports/$id", params: { id } },
          )
        }
        className="-mt-2 inline-flex w-fit items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={12} />
        {isScan
          ? "Back to scan — add or re-shoot cards"
          : "Back to Upload — fix photos or row count"}
      </button>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Bin + Lot pickers — final assignment before commit. Scan sessions
          don't use lots (no purchase batch), so only the container shows. */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        <h2 className="text-sm font-semibold text-foreground">
          {isScan ? "Assign bin" : "Assign bin + lot"}
        </h2>
        <p className="text-xs text-muted-foreground">
          {isScan
            ? "Pick the container these scanned cards land in. Cards arrive in Unlisted status."
            : "One container and one lot apply to every card landing from this import. Cards arrive in Unlisted status."}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">
              Container
            </span>
            <select
              value={batchBinId}
              onChange={(e) => setBatchBinId(e.target.value)}
              className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
            >
              <option value="">— Unsorted (no container) —</option>
              {targetOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {!isScan && (
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">
                Lot
              </span>
              <select
                value={batchLotId}
                onChange={(e) => setBatchLotId(e.target.value)}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">— No lot —</option>
                {lots.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.purchaseDate ? `${l.purchaseDate.slice(0, 10)} · ` : ""}
                    {l.seller || "Lot"} ($
                    {calcLandedCost(l).toFixed(2)})
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      {/* Wide-row list — one row per item */}
      {sortedItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-card/40 py-12 text-center">
          <ImageIcon size={28} className="text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">
            Nothing to review
          </p>
          <p className="text-xs text-muted-foreground">
            Every row was deleted on Upload. Go back and re-upload, or discard.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {scoredItems.map(({ item, parsed, score }) => (
            <ImportMatchRow
              key={item.id}
              item={item}
              parsed={parsed}
              score={score}
              binName={
                batchBinId
                  ? (containerById.get(batchBinId)?.name ?? null)
                  : null
              }
              onConditionChange={(condition) =>
                void handleField(item, { condition })
              }
              onFinishChange={(finish) =>
                void handleField(item, {
                  finish: finish === "Normal" ? null : finish,
                })
              }
              onNameChange={(name) => void handleField(item, { name })}
              onQuickView={() => setQuickViewItem(item)}
              onRevision={
                isScan && !(parsed.name ?? "").trim() && item.imageFrontRef
                  ? () => void revisionItem(item)
                  : undefined
              }
              revisioning={revisioning.has(item.id)}
            />
          ))}
        </div>
      )}

      <StickyConfirmBar
        rows={sortedItems.length}
        totalUnits={totalUnits}
        confirming={confirming}
        disabled={confirming || sortedItems.length === 0}
        onConfirm={() => void handleConfirm()}
      />

      {quickViewItem &&
        (() => {
          const p = (quickViewItem.parsed ?? {}) as ParsedShape;
          // TNK-141 phase E — surface the server's pre-resolved catalog
          // candidate as the default Identified selection. Lets the modal
          // open with the worker's pick already locked in, no search
          // round-trip needed.
          const preResolved = quickViewItem.catalog
            ? (quickViewItem.catalog as unknown as CatalogCandidate)
            : null;
          return (
            <QuickViewModal
              open
              onClose={() => setQuickViewItem(null)}
              initial={{
                name: p.name,
                setName: p.setName,
                cardNumber: p.cardNumber,
                game: p.game,
              }}
              csv={
                isScan
                  ? {
                      name: p.name,
                      setName: p.setName,
                      cardNumber: p.cardNumber,
                    }
                  : pickCsvFields(quickViewItem.csvRow ?? {})
              }
              sourceLabel={isScan ? "Scanned" : undefined}
              uploadedImageRef={quickViewItem.imageFrontRef ?? null}
              preResolved={preResolved}
              identified={{
                name: p.name,
                setName: p.setName,
                cardNumber: p.cardNumber,
                year: p.year,
                rarity: p.rarity,
                artist: p.tcgArtist,
                type: p.tcgSupertype,
              }}
              onSelect={(candidate: CatalogCandidate, acceptedFields) => {
                const itemId = quickViewItem.id;
                setQuickViewItem(null);
                // TNK-141 phase F — granular field-level merge. Build a
                // partial parsed-shape update from only the fields the
                // operator left checked. Image refs stay tied to the
                // name acceptance (the catalog thumb only makes sense
                // when we're also taking the catalog's name).
                const partial: Partial<ParsedShape> = {};
                if (acceptedFields.has("name")) {
                  partial.name = candidate.name;
                  partial.matchImageUrl = candidate.imageSmall;
                  partial.matchImageLargeUrl = candidate.imageLarge;
                }
                if (acceptedFields.has("set")) {
                  partial.setName = candidate.setName;
                  partial.setCode = candidate.setId;
                }
                if (acceptedFields.has("cardNumber")) {
                  partial.cardNumber = candidate.number;
                }
                if (acceptedFields.has("year")) {
                  partial.year = candidate.releasedAt?.slice(0, 4);
                }
                if (acceptedFields.has("rarity")) {
                  partial.rarity = candidate.rarity;
                }
                if (acceptedFields.has("type")) {
                  partial.tcgSupertype = candidate.supertype;
                  partial.tcgTypes = candidate.types;
                }
                if (acceptedFields.has("artist")) {
                  partial.tcgArtist = candidate.artist;
                }
                if (isScan) {
                  // Picking a printing resolves the scan's review flag → the
                  // row's confidence flips to ready.
                  partial.needsReview = false;
                  partial.confidence = "high";
                }
                // Optimistic merge in-place + flip status to "fixed".
                setItems((prev) =>
                  prev.map((it) =>
                    it.id === itemId
                      ? {
                          ...it,
                          parsed: { ...(it.parsed ?? {}), ...partial },
                          enrichmentStatus: "fixed",
                        }
                      : it,
                  ),
                );
                // Decide which endpoint fits the operator's intent:
                //   - All catalog fields accepted (or nothing changed)
                //     → /override, which stamps catalog + catalogMatch
                //     server-side too.
                //   - Partial accept → PATCH parsed with only the
                //     checked fields; catalogMatch stays whatever the
                //     worker chose so the chip keeps its meaning.
                const populatedCount = [
                  candidate.name && "name",
                  (candidate.setName || candidate.setId) && "set",
                  candidate.number && "cardNumber",
                  candidate.releasedAt && "year",
                  candidate.rarity && "rarity",
                  (candidate.supertype || candidate.types?.length) && "type",
                  candidate.artist && "artist",
                ].filter(Boolean).length;
                const isFullAccept = acceptedFields.size === populatedCount;
                // Scan ALWAYS patches (never /override): /override re-fetches
                // the catalog STOCK image, which would replace the operator's
                // actual scan photo. Patch updates the fields + keeps the photo.
                const op =
                  !isScan && isFullAccept
                    ? overrideImportItem(id, itemId, candidate.id)
                    : patchImportItem(
                        id,
                        itemId,
                        partial as Record<string, unknown>,
                      );
                void op
                  .then((next) => {
                    setItems((prev) =>
                      prev.map((it) => (it.id === next.id ? next : it)),
                    );
                  })
                  .catch((err) => {
                    setError((err as Error).message ?? "Update failed.");
                  });
              }}
            />
          );
        })()}
    </section>
  );
}

/* ─────────────────────────────  Components  ──────────────────────────────── */

function ImportMatchRow({
  item,
  parsed,
  score,
  binName,
  onConditionChange,
  onFinishChange,
  onNameChange,
  onQuickView,
  onRevision,
  revisioning,
}: {
  item: ApiImportDraftItem;
  parsed: ParsedShape;
  score: number;
  binName: string | null;
  onConditionChange: (c: string) => void;
  onFinishChange: (f: FinishOption) => void;
  onNameChange: (name: string) => void;
  onQuickView: () => void;
  /** TNK-216 — provided only for blank scan rows: re-run the vision identify
   *  on the stored image. Undefined hides the button (CSV / already-named). */
  onRevision?: () => void;
  revisioning?: boolean;
}) {
  const front = item.imageFrontRef
    ? imageUrl(item.imageFrontRef, "thumb")
    : null;
  const tone = confidenceTone(score);
  const toneBadge =
    tone === "high"
      ? { bg: "rgba(74,222,128,0.9)", color: "#000", glow: FIGMA.good }
      : tone === "medium"
        ? { bg: "rgba(250,204,21,0.9)", color: "#000", glow: FIGMA.warn }
        : { bg: "rgba(248,113,113,0.9)", color: "#000", glow: FIGMA.bad };
  const matchGlow = `0 0 18px ${toneBadge.glow}40, 0 0 4px ${toneBadge.glow}30`;
  // TNK-141 phase E — prefer the server-persisted catalog candidate's
  // image when available (worker resolves once per row, no GET-side
  // backfill needed). Fall back to the legacy `matchImageUrl` that
  // backfillMatchImages stamps on the response.
  const matchSrc =
    item.catalog?.imageSmall ??
    (parsed.matchImageUrl as string | undefined) ??
    null;
  const catalogMatch = item.catalogMatch ?? null;

  const rarityCfg = parsed.rarity
    ? rarityConfig[asRarity(parsed.rarity)]
    : null;
  const cardShadow = rarityCfg
    ? `0 8px 24px rgba(0,0,0,0.3), 0 0 32px ${rarityCfg.glow}`
    : "0 4px 16px rgba(0,0,0,0.2)";
  const cardBorder = rarityCfg ? `${rarityCfg.color}33` : FIGMA.panelBorder;
  const isMagic = parsed.game === "Magic";
  const mtgStripe = isMagic ? mtgBorderGradient(parsed.mtgColors) : null;

  const currentFinish: FinishOption =
    parsed.finish === "Holo" ||
    parsed.finish === "Reverse Holo" ||
    parsed.finish === "Premium"
      ? (parsed.finish as FinishOption)
      : "Normal";
  const showFinish = !!parsed.rarity || isMagic;

  // Yellow warning banner threshold — any confidence below 85% earns a
  // prominent "verify match" call-out in the right sub-column. Tighter
  // than the previous medium-tone (75–89) trigger.
  const showWarning = score < 85;

  return (
    <div
      className="relative flex flex-col overflow-hidden rounded-xl transition-all sm:flex-row sm:items-stretch"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: `1px solid ${cardBorder}`,
        boxShadow: cardShadow,
      }}
    >
      {/* Magic color identity stripe along the left edge */}
      {mtgStripe && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-1 z-10"
          style={{ background: mtgStripe }}
        />
      )}

      {/* ── LEFT PANEL: thumbnails + lookup, dark background, vertical
            divider on the right edge so it reads as its own zone ── */}
      <div
        className="flex shrink-0 flex-col items-center gap-3 border-b border-border p-3 sm:border-b-0 sm:border-r"
        style={{ background: "rgba(0,0,0,0.28)", minWidth: 200 }}
      >
        <div className="flex items-start gap-2">
          {/* Uploaded photo */}
          <div className="flex flex-col items-center gap-1">
            {front ? (
              <img
                src={front}
                alt="Uploaded photo"
                className="h-[112px] w-[80px] rounded border border-border object-contain"
              />
            ) : (
              <div className="flex h-[112px] w-[80px] items-center justify-center rounded border border-dashed border-border text-muted-foreground/40">
                <ImageIcon size={14} />
              </div>
            )}
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
              Uploaded
            </span>
          </div>

          {/* Catalog match */}
          <div className="flex flex-col items-center gap-1">
            <div className="relative">
              {matchSrc ? (
                <img
                  src={matchSrc}
                  alt="Catalog match"
                  className="h-[112px] w-[80px] rounded object-contain"
                  style={{
                    border: `1px solid ${toneBadge.glow}40`,
                    boxShadow: matchGlow,
                  }}
                />
              ) : (
                // TNK-141 verify follow-up: a bare ImageIcon placeholder reads
                // as "image failed to load" on rows where the worker correctly
                // returned no match. Spell out the intentional empty state so
                // the operator knows to use the Name editor's search affordance.
                <div className="flex h-[112px] w-[80px] flex-col items-center justify-center gap-1 rounded border border-dashed border-border px-1 text-center text-muted-foreground/70">
                  <ImageIcon size={14} />
                  <span className="text-[9px] leading-tight">
                    No catalog
                    <br />
                    match
                  </span>
                </div>
              )}
              <div
                className="absolute -right-1.5 -top-1.5 flex h-6 min-w-[24px] items-center justify-center rounded-full px-1"
                style={{
                  background: toneBadge.bg,
                  color: toneBadge.color,
                  fontFamily: FIGMA.fontMono,
                  fontSize: 9,
                  fontWeight: 800,
                }}
                title={`Match confidence: ${score}/100`}
              >
                {score}
              </div>
            </div>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
              Match
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={onQuickView}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 transition-all"
          style={{
            background: FIGMA.indigoBg,
            border: `1px solid ${FIGMA.indigoBorder}`,
            color: FIGMA.indigoLight,
            fontFamily: FIGMA.fontBody,
            fontSize: 11,
            fontWeight: 600,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = FIGMA.indigoBgHover;
            e.currentTarget.style.borderColor = FIGMA.indigoBorderStrong;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = FIGMA.indigoBg;
            e.currentTarget.style.borderColor = FIGMA.indigoBorder;
          }}
        >
          <Eye size={12} />
          View / Lookup
        </button>

        {onRevision && (
          <button
            type="button"
            onClick={onRevision}
            disabled={revisioning}
            className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition-all disabled:opacity-60"
            style={{
              background: "rgba(250,204,21,0.10)",
              borderColor: "rgba(250,204,21,0.35)",
              color: "#d8a300",
              fontFamily: FIGMA.fontBody,
            }}
          >
            {revisioning ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            {revisioning ? "Scanning…" : "Re-run vision"}
          </button>
        )}
      </div>

      {/* ── RIGHT PANEL: split into left sub-col (name + meta) and
            right sub-col (variant + price/qty + condition + warning) ── */}
      <div className="flex flex-1 flex-col gap-3 p-4 sm:flex-row">
        {/* LEFT SUB-COLUMN — name (heading + editable input with
            Copy/Search icons) + read-only meta rows */}
        <div className="flex flex-1 flex-col gap-2 min-w-0">
          <div className="flex items-center gap-2">
            <h3
              className="truncate"
              style={{
                fontFamily: FIGMA.fontHeading,
                fontSize: 18,
                fontWeight: 700,
                color: FIGMA.fg,
                lineHeight: 1.15,
                letterSpacing: "0.01em",
              }}
              title={parsed.name || "—"}
            >
              {parsed.name || "—"}
            </h3>
            {catalogMatch && <CatalogMatchChip tier={catalogMatch} />}
          </div>

          <NameEditor
            initial={parsed.name ?? ""}
            onCommit={onNameChange}
            onSearch={onQuickView}
          />

          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1 text-[11px] sm:grid-cols-3">
            {parsed.tcgSupertype && (
              <Meta label="Type" value={parsed.tcgSupertype} />
            )}
            <Meta label="Set" value={parsed.setName} />
            <Meta label="#" value={parsed.cardNumber} />
            {parsed.year && <Meta label="Year" value={parsed.year} />}
            <Meta
              label="Rarity"
              value={parsed.rarity}
              valueColor={rarityCfg?.color}
            />
            {parsed.tcgArtist && (
              <Meta label="Illustrator" value={parsed.tcgArtist} />
            )}
          </dl>
        </div>

        {/* RIGHT SUB-COLUMN — variant, price/qty, bin + condition,
            warning banner when confidence < 85% */}
        <div className="flex shrink-0 flex-col gap-2 sm:w-[210px]">
          {showFinish && (
            <label className="flex items-center justify-between gap-2 text-[11px]">
              <span style={{ color: FIGMA.muted }}>Variant</span>
              <select
                value={currentFinish}
                onChange={(e) => onFinishChange(e.target.value as FinishOption)}
                className="appearance-none rounded-md px-2.5 py-1"
                style={{
                  background: "#0d1220",
                  border: `1px solid ${FIGMA.indigoBorder}`,
                  color:
                    currentFinish === "Normal"
                      ? FIGMA.muted
                      : FIGMA.indigoLight,
                  fontFamily: FIGMA.fontBody,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  outline: "none",
                }}
                aria-label="Variant"
              >
                {FINISH_OPTIONS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="flex flex-col">
              <span style={{ color: FIGMA.dim, fontSize: 9 }}>PRICE</span>
              <span
                style={{
                  fontFamily: FIGMA.fontMono,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#c9a84c",
                }}
              >
                ${(parsed.marketPrice ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="flex flex-col">
              <span style={{ color: FIGMA.dim, fontSize: 9 }}>QTY</span>
              <span
                style={{
                  fontFamily: FIGMA.fontMono,
                  fontSize: 13,
                  fontWeight: 600,
                  color: FIGMA.fg,
                }}
              >
                {parsed.quantity ?? 1}
              </span>
            </div>
          </div>

          {binName && (
            <div className="flex items-center justify-between gap-2 text-[11px]">
              <span style={{ color: FIGMA.muted }}>Bin</span>
              <span
                className="truncate"
                style={{
                  fontFamily: FIGMA.fontBody,
                  fontSize: 11,
                  fontWeight: 600,
                  color: FIGMA.indigoLight,
                }}
                title={binName}
              >
                {binName}
              </span>
            </div>
          )}

          <label className="flex items-center justify-between gap-2 text-[11px]">
            <span style={{ color: FIGMA.muted }}>Condition</span>
            <ConditionSelect
              value={(parsed.condition as ConditionOption) ?? "NM"}
              onChange={onConditionChange}
            />
          </label>

          {showWarning && (
            <div
              className="mt-1 flex items-start gap-1.5 rounded-md px-2 py-1.5"
              style={{
                background: FIGMA.warnBg,
                border: `1px solid ${FIGMA.warnBorder}`,
                color: FIGMA.warn,
              }}
            >
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span
                style={{
                  fontFamily: FIGMA.fontBody,
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                Verify match — confidence {score}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Editable name field with Copy + Search icon buttons. Local state
 * keeps the input snappy; commits on blur or Enter so we don't fire
 * a PATCH on every keystroke. Re-syncs when the underlying parsed
 * name changes (e.g. after a Quick view override).
 */
function NameEditor({
  initial,
  onCommit,
  onSearch,
}: {
  initial: string;
  onCommit: (next: string) => void;
  onSearch: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== initial) onCommit(trimmed);
  };

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard API can be blocked (insecure contexts, permissions).
      // Silent failure is fine — the input itself is still selectable.
    }
  };

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="Card name"
        className="flex-1 rounded-md px-2 py-1 outline-none focus:border-primary"
        style={{
          background: "#0d1220",
          border: `1px solid ${FIGMA.panelBorder}`,
          color: FIGMA.fgSecondary,
          fontFamily: FIGMA.fontBody,
          fontSize: 12,
        }}
        aria-label="Edit card name"
      />
      <button
        type="button"
        onClick={() => void copy()}
        title={copied ? "Copied" : "Copy name"}
        className="flex h-7 w-7 items-center justify-center rounded-md transition-all"
        style={{
          background: copied ? FIGMA.goodBg : "rgba(255,255,255,0.04)",
          border: `1px solid ${copied ? FIGMA.goodBorder : FIGMA.panelBorder}`,
          color: copied ? FIGMA.good : FIGMA.muted,
        }}
      >
        <Copy size={12} />
      </button>
      <button
        type="button"
        onClick={onSearch}
        title="Search catalog for this name"
        className="flex h-7 w-7 items-center justify-center rounded-md transition-all"
        style={{
          background: FIGMA.indigoBg,
          border: `1px solid ${FIGMA.indigoBorder}`,
          color: FIGMA.indigoLight,
        }}
      >
        <Search size={12} />
      </button>
    </div>
  );
}

/**
 * TNK-140 phase E — color-coded condition select. NM=green, LP=lime,
 * MP=yellow, HP=orange, Damaged=red. Matches the Figma's
 * ConditionSelect component — operator scans a row and sees condition
 * at a glance instead of reading two letters.
 */
const CONDITION_COLORS: Record<ConditionOption, string> = {
  NM: "#4ade80",
  LP: "#a3e635",
  MP: "#facc15",
  HP: "#fb923c",
  Damaged: "#f87171",
};

function ConditionSelect({
  value,
  onChange,
}: {
  value: ConditionOption;
  onChange: (next: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none rounded px-2 py-1"
      style={{
        background: "#0d1220",
        border: `1px solid ${FIGMA.panelBorderHover}`,
        color: CONDITION_COLORS[value],
        fontFamily: FIGMA.fontMono,
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
        outline: "none",
      }}
      aria-label="Condition"
    >
      {CONDITIONS.map((c) => (
        <option key={c} value={c} style={{ color: CONDITION_COLORS[c] }}>
          {c}
        </option>
      ))}
    </select>
  );
}

/**
 * TNK-141 phase E — small chip surfacing the server-side
 * `catalogMatch` classification beside the row's confidence pill.
 *
 *   - exact    → green   "EXACT"     (worker found a single clear hit)
 *   - manual   → green   "FIXED"     (operator overrode via Quick view)
 *   - multiple → indigo  "MULTIPLE"  (worker couldn't pick — pick yourself)
 *   - none     → red     "NO MATCH"  (worker tried, upstream returned 0)
 */
function CatalogMatchChip({
  tier,
}: {
  tier: "exact" | "multiple" | "none" | "manual" | string;
}) {
  const palette =
    tier === "exact" || tier === "manual"
      ? { bg: FIGMA.goodBg, border: FIGMA.goodBorder, fg: FIGMA.good }
      : tier === "multiple"
        ? {
            bg: "rgba(99,102,241,0.12)",
            border: FIGMA.indigoBorder,
            fg: FIGMA.indigoLight,
          }
        : tier === "none"
          ? { bg: FIGMA.badBg, border: FIGMA.badBorder, fg: FIGMA.bad }
          : null;
  if (!palette) return null;
  const label =
    tier === "exact"
      ? "EXACT"
      : tier === "manual"
        ? "FIXED"
        : tier === "multiple"
          ? "MULTIPLE"
          : "NO MATCH";
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        fontFamily: FIGMA.fontMono,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.04em",
      }}
      title={`Server-side catalog match: ${tier}`}
    >
      {label}
    </span>
  );
}

function Meta({
  label,
  value,
  valueColor,
}: {
  label: string;
  value?: string;
  valueColor?: string;
}) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5">
      <dt style={{ color: FIGMA.dim, fontSize: 10 }}>{label}</dt>
      <dd
        className="truncate"
        style={{
          color: valueColor ?? FIGMA.muted,
          fontSize: 11,
          fontWeight: valueColor ? 600 : 400,
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function SummaryChip({
  count,
  tone,
  label,
}: {
  count: number;
  tone: "success" | "warning";
  label: string;
}) {
  const palette =
    tone === "success"
      ? { bg: FIGMA.goodBg, border: FIGMA.goodBorder, fg: FIGMA.good }
      : { bg: FIGMA.warnBg, border: FIGMA.warnBorder, fg: FIGMA.warn };
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        fontFamily: FIGMA.fontMono,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      <CheckCircle2 size={12} />
      {count} {label}
    </span>
  );
}

function StickyConfirmBar({
  rows,
  totalUnits,
  confirming,
  disabled,
  onConfirm,
}: {
  rows: number;
  totalUnits: number;
  confirming: boolean;
  disabled: boolean;
  onConfirm: () => void;
}) {
  return (
    /* TNK-141 verify follow-up: was `fixed inset-x-0 bottom-0` which
       overlapped the last row's action buttons (View/Lookup). No amount
       of bottom padding on the section reliably cleared it because the
       outer layout's scroll-container relationship made the padding
       inconsistent. `sticky bottom-0` lives in normal document flow
       (claims its own space at the end of the section + sticks to the
       viewport bottom when the section is taller than the viewport),
       giving the same always-visible behavior without overlap. */
    <div className="sticky inset-x-0 bottom-0 z-30 -mx-4 mt-4 border-t border-border bg-background/95 px-6 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.2)] backdrop-blur md:-mx-6">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <span style={{ color: FIGMA.muted, fontSize: 12 }}>
          <span style={{ fontFamily: FIGMA.fontMono }}>{rows}</span> card
          {rows === 1 ? "" : "s"} ·{" "}
          <span style={{ fontFamily: FIGMA.fontMono }}>{totalUnits}</span> total
          unit{totalUnits === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          onClick={onConfirm}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 transition-all disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: FIGMA.indigoGradient,
            border: "none",
            color: "#fff",
            fontFamily: FIGMA.fontBody,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: FIGMA.indigoShadow,
          }}
          onMouseEnter={(e) => {
            if (!disabled)
              e.currentTarget.style.boxShadow = FIGMA.indigoShadowHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = FIGMA.indigoShadow;
          }}
        >
          <CheckCircle2 size={16} />
          {confirming
            ? "Adding to inventory…"
            : `Add ${rows} card${rows === 1 ? "" : "s"} to inventory`}
        </button>
      </div>
    </div>
  );
}
